/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { inject } from '@angular/core';
import { CanMatchFn } from '@angular/router';
import { ORGANIZATION_WAIT_MS, UserAuthState } from '@app/app/user-auth/store/user-auth.state';
import { Store } from '@ngxs/store';
import { of } from 'rxjs';
import { delay, map, timeout } from 'rxjs/operators';
import { ContentHelper } from '../helpers/content-helper';
import { VersionHelper } from '../helpers/version.helper';

/**
 * Holds the app frame back on a browser's first visit to the org, until `MainComponent` has stored its version. Matching
 * loads the lazy modules, and many pick their routes from that flag once, when they load: on a first visit to a deep link
 * such as Open in new tab, a V3 org would otherwise run them as V2 for as long as the tab stays open.
 *
 * An iframe doesn't wait: a launch that needs the organisation waits for it itself, and any other keeps its timing.
 */
export const organizationVersionReadyGuard: CanMatchFn = () => {
  if (ContentHelper.isFrameMode() || VersionHelper.getCurrentUIVersion()) {
    return true;
  }
  return UserAuthState.organizationSettled$(inject(Store)).pipe(
    // This fires inside the load, and `MainComponent` stores the version once that has finished.
    delay(0),
    map(() => true),
    timeout({ first: ORGANIZATION_WAIT_MS, with: () => of(true) }),
  );
};
